'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useAccount } from 'wagmi';
import { EventData, initSampleEvents, clearSampleEvents } from '@/utils/initSampleEvents';



export default function EventList() {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(false);
  const { address, isConnected } = useAccount();

  useEffect(() => {
    // Khởi tạo sample events nếu chưa có
    if (typeof window !== 'undefined') {
      initSampleEvents();
      loadEvents();
    }
  }, []);

  const loadEvents = () => {
    setLoading(true);
    try {
      // Check if we're in browser environment
      if (typeof window === 'undefined') {
        setLoading(false);
        return;
      }
      
      const savedEvents = localStorage.getItem('xoso_events');
      if (savedEvents) {
        const parsedEvents = JSON.parse(savedEvents);
        // Sắp xếp theo thời gian tạo mới nhất
        const sortedEvents = parsedEvents.sort((a: EventData, b: EventData) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setEvents(sortedEvents);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const addEvent = (eventData: Omit<EventData, 'id' | 'createdAt'>) => {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      return;
    }

    const newEvent: EventData = {
      ...eventData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
    };

    const updatedEvents = [newEvent, ...events];
    setEvents(updatedEvents);
    
    try {
      localStorage.setItem('xoso_events', JSON.stringify(updatedEvents));
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const deleteEvent = (eventId: string) => {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      return;
    }

    const updatedEvents = events.filter(event => event.id !== eventId);
    setEvents(updatedEvents);
    
    try {
      localStorage.setItem('xoso_events', JSON.stringify(updatedEvents));
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handleClearSampleEvents = () => {
    if (clearSampleEvents()) {
      loadEvents();
    }
  };

  // Expose addEvent function globally for use in other components
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).addEventToList = addEvent;
      (window as any).refreshEventList = loadEvents;
      return () => {
        delete (window as any).addEventToList;
        delete (window as any).refreshEventList;
      };
    }
  }, [events]);

  if (!isConnected) {
    return (
      <div className="max-w-4xl mx-auto p-6 rounded-2xl shadow-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(192,132,252,0.2)] backdrop-blur-md text-white" style={{background: 'linear-gradient(135deg, #181028 60%, #2d1a4a 100%)'}}>
        <div className="text-center">
          <h3 className="text-xl font-bold mb-2">Danh sách sự kiện</h3>
          <p className="text-purple-200">Kết nối ví để xem danh sách sự kiện</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 rounded-2xl shadow-xl bg-[rgba(255,255,255,0.05)] border border-[rgba(192,132,252,0.2)] backdrop-blur-md text-white" style={{background: 'linear-gradient(135deg, #181028 60%, #2d1a4a 100%)'}}>
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-blue-600 opacity-20 rounded-xl transform -skew-y-3"></div>
        <div className="relative">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400">Danh sách sự kiện</h3>
            <div className="flex space-x-2">
              <button
                onClick={loadEvents}
                disabled={loading}
                className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 rounded-lg transition-colors"
              >
                {loading ? 'Đang tải...' : 'Làm mới'}
              </button>
              {events.some(e => e.id.startsWith('sample_')) && (
                <button
                  onClick={handleClearSampleEvents}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Xóa mẫu
                </button>
              )}
            </div>
          </div>
          <p className="text-center text-purple-200">Các sự kiện đã được tạo</p>
          {events.length > 0 && (
            <div className="text-center mt-2">
              <span className="text-sm text-purple-300">
                Tổng cộng: {events.length} sự kiện
                {address && (
                  <span className="ml-2 text-green-300">
                    • {events.filter(e => e.creator === address).length} của bạn
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-purple-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <p className="text-purple-200 mb-4">Chưa có sự kiện nào được tạo</p>
          <Link href="/event/create">
            <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:scale-105 transition">
              Tạo sự kiện đầu tiên
            </button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div key={event.id} className="bg-white/10 border border-purple-700 rounded-xl p-4 hover:bg-white/15 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-semibold text-white">{event.name}</h4>
                                         <div className="flex items-center space-x-2">
                       <span className="text-xs text-purple-300 bg-purple-900/50 px-2 py-1 rounded">
                         {new Date(event.createdAt).toLocaleDateString('vi-VN')}
                       </span>
                       {event.id.startsWith('sample_') && (
                         <span className="text-xs text-yellow-300 bg-yellow-900/50 px-2 py-1 rounded">
                           Mẫu
                         </span>
                       )}
                       {event.creator === address && (
                         <span className="text-xs text-green-300 bg-green-900/50 px-2 py-1 rounded">
                           Của bạn
                         </span>
                       )}
                     </div>
                  </div>
                  
                  <p className="text-purple-200 text-sm mb-3 line-clamp-2">{event.description}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-purple-300">Giá vé:</span>
                      <div className="font-semibold text-green-400">{event.price} ETH</div>
                    </div>
                    <div>
                      <span className="text-purple-300">Tổng vé:</span>
                      <div className="font-semibold text-white">{event.totalTickets}</div>
                    </div>
                    <div>
                      <span className="text-purple-300">Contract:</span>
                      <div className="font-mono text-xs text-blue-300 truncate">
                        {event.contractAddress.slice(0, 6)}...{event.contractAddress.slice(-4)}
                      </div>
                    </div>
                    <div>
                      <span className="text-purple-300">Người tạo:</span>
                      <div className="font-mono text-xs text-purple-300 truncate">
                        {event.creator.slice(0, 6)}...{event.creator.slice(-4)}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col space-y-2 ml-4">
                  <Link href={`/event/detail?contract=${event.contractAddress}`}>
                    <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:scale-105 transition text-sm">
                      Xem chi tiết
                    </button>
                  </Link>
                  
                  {event.creator === address && (
                    <button
                      onClick={() => deleteEvent(event.id)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition text-sm"
                    >
                      Xóa
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 